import { useState, useRef, useEffect } from "react";

// ── Palette YOMA ──────────────────────────────────────────────────────────────
const GOLD   = "#C9A96E";
const INK    = "#1C1915";
const WARM   = "#F8F4EE";
const BORDER = "#E7E1D8";
const MUTED  = "#8B7361";
const WHITE  = "#FFFFFF";
const SOFT   = "#F2EDE6";

// ── Contrainte système — injectée dans chaque prompt ─────────────────────────
const SYS =
`ABSOLUTE RULES — never violate:
1. Preserve the EXACT camera angle, position, focal length and perspective of the reference photo.
2. Keep ALL background elements 100% identical: sky, clouds, sun position, neighboring buildings, street, parked vehicles, trees, fences.
3. Same time of day and lighting conditions as the reference.
4. ONLY add or transform elements DIRECTLY on the construction plot visible in the image.
5. Output must look like a real, professional on-site construction photograph — photorealistic, no cartoon, no illustration.`;

// ── Master Prompts YOMA ───────────────────────────────────────────────────────
const PHASES_CFG = [
  {
    id: 1,
    icon: "⛏️",
    name: "Terrain & Fondations",
    accent: "#8B6E4E",
    masterPrompt:
`${SYS}

PHASE 1 of 5 — Site preparation & foundations:
Transform the terrain into a construction site at its earliest stage. Show ONLY on the plot:
• Excavated foundation trenches (fouilles en tranchées)
• Wooden formwork panels (coffrages en bois) around the perimeter
• Steel reinforcement bars (ferraillage / armatures HA) protruding 30–50 cm from the ground
• Fresh grey concrete footings being poured (semelles filantes)
• Muddy disturbed soil around the excavation
• Orange safety fencing around the perimeter of the site
• A small yellow construction excavator or materials storage area (optional)
No walls. No structure above ground level.`,
  },
  {
    id: 2,
    icon: "🧱",
    name: "Élévation Gros Œuvre",
    accent: "#B07040",
    masterPrompt:
`${SYS}

PHASE 2 of 5 — Structural frame (Gros Œuvre):
Build upon the foundations from the reference image. Show ONLY on the plot:
• Concrete block walls (murs en parpaings creux) rising 3–4 meters
• Visible concrete columns (poteaux en béton) at corners and intervals
• Horizontal ring beams (chaînages) at window sill and lintel levels
• Rough window and door openings with concrete lintels (linteaux)
• Metal tubular scaffolding (échafaudage tubulaire) on at least two façades
• Grey, unfinished raw concrete and block appearance
• No roof yet. No windows installed. Bare structure only.`,
  },
  {
    id: 3,
    icon: "🏗️",
    name: "Mise Hors d'Eau",
    accent: "#4A7A50",
    masterPrompt:
`${SYS}

PHASE 3 of 5 — Weatherproofing (Mise hors d'eau):
Complete the roof structure on top of the walls from the reference image. Show ONLY on the plot:
• Flat concrete roof slab (dalle de compression en béton) with visible top formwork recently removed, OR a sloped timber roof framework (charpente en bois) with terracotta tiles
• The building envelope is now fully closed and weathertight
• Scaffolding partially dismantled or fully removed
• Window openings temporarily covered with plastic sheeting or plywood
• Exterior walls remain bare grey concrete block
• Construction debris beginning to be cleared
• The building silhouette is now complete.`,
  },
  {
    id: 4,
    icon: "🪟",
    name: "Second Œuvre",
    accent: "#2A5A80",
    masterPrompt:
`${SYS}

PHASE 4 of 5 — Exterior finishing (Second œuvre):
Apply exterior finishes to the structure from the reference image. Show ONLY on the plot:
• Smooth exterior plaster and render (enduit de façade gratté fin) in warm cream or sand beige color covering all walls
• Installed black matte aluminum double-glazed window frames with clean sightlines
• Anthracite or dark charcoal aluminum front door with sidelight panel
• Exterior roller shutters or aluminum shutters (volets roulants) in dark grey
• Exposed zinc or aluminium drainage gutters and downpipes (gouttières, descentes EP)
• No landscaping yet — bare earth or compacted gravel around building
• The building looks externally finished but the garden is empty.`,
  },
  {
    id: 5,
    icon: "✨",
    name: "Finitions & Paysagisme",
    accent: "#6A3A80",
    masterPrompt:
`${SYS}

PHASE 5 of 5 — Final YOMA luxury architectural render (Finitions & Paysagisme):
Add luxury finishes and landscaping to the completed building from the reference image. Show ONLY on the plot:
• Manicured Mediterranean garden: olive trees, ornamental grasses, agapanthus, lavender, rosemary, succulents
• Stone or polished concrete paved driveway and pathways (dalle en pierre naturelle ou béton désactivé)
• Decorative white gravel or pebble garden beds with stone edging
• Exterior wall-mounted LED light fixtures (appliques murales design) casting warm glow
• LED pathway lighting along the driveway (bornes lumineuses)
• White-rendered perimeter wall with modern dark iron sliding gate and video intercom column
• Potted Buxus or olive trees framing the entrance door
• Optionally: swimming pool terrace with wooden deck and sun loungers in background
Style: high-end contemporary Moroccan luxury residential photography, golden hour light.`,
  },
];

function initPhases() {
  return PHASES_CFG.map(p => ({
    ...p,
    prompt: p.masterPrompt,
    image: null,
    status: "pending",
    validated: false,
    note: "",
  }));
}

const ST = {
  pending:    { bg: "#F0EDE8", color: MUTED,     label: "En attente" },
  generating: { bg: "#FFF8E1", color: "#7A5800",  label: "Génération…" },
  done:       { bg: "#E8F5E9", color: "#2E7D32",  label: "Généré ✓" },
  error:      { bg: "#FFEBEE", color: "#B85C3A",  label: "Erreur ✗" },
};

const PROJ_TYPES = [
  "Villa individuelle", "Villa R+1", "Villa R+2", "Immeuble R+3",
  "Immeuble R+4 et +", "Maison en rangée", "Local commercial", "Bureau / Bureau",
];
const STYLES = [
  "Contemporain marocain", "Moderne minimaliste", "Traditionnel (Riad)",
  "Neo-classique", "Méditerranéen", "Industriel", "Écologique / Bioclimatique",
];

// ── Composant principal ───────────────────────────────────────────────────────
export default function VisualisationTemporelleAIPage({ cabinetId }) {
  const [provider,      setProvider]      = useState(() => localStorage.getItem("yoma_tl_provider")      || "stability");
  const [stabilityKey,  setStabilityKey]  = useState(() => localStorage.getItem("yoma_stability_key")    || "");
  const [stabilityModel,setStabilityModel]= useState(() => localStorage.getItem("yoma_stability_model")  || "core");
  const [apiKey,        setApiKey]        = useState(() => localStorage.getItem("yoma_gemini_key")        || "");
  const [showKey,       setShowKey]       = useState(false);
  const [model,         setModel]         = useState("gemini-2.0-flash-exp-image-generation");
  const [testResult,    setTestResult]    = useState(null);
  const [testMsg,       setTestMsg]       = useState("");

  // Contexte projet
  const [projName,  setProjName]  = useState("");
  const [clientNm,  setClientNm]  = useState("");
  const [projType,  setProjType]  = useState(() => localStorage.getItem("yoma_proj_type")  || "Villa individuelle");
  const [projStyle, setProjStyle] = useState(() => localStorage.getItem("yoma_proj_style") || "Contemporain marocain");
  const [projLoc,   setProjLoc]   = useState(() => localStorage.getItem("yoma_proj_loc")   || "Maroc");
  const [holdDur,   setHoldDur]   = useState(() => parseInt(localStorage.getItem("yoma_hold_dur") || "3", 10));

  const [terrainImg, setTerrainImg] = useState(null);
  const [phases,    setPhases]    = useState(initPhases);
  const [generating, setGenerating] = useState(null);
  const [assembling, setAssembling] = useState(false);
  const [asmPct,    setAsmPct]    = useState(0);
  const [videoUrl,  setVideoUrl]  = useState(null);
  const [error,     setError]     = useState("");
  const [expandedPh, setExpandedPh] = useState(null);

  const terrainRef = useRef();
  const videoRef   = useRef();

  // Persist
  useEffect(() => { if (apiKey)        localStorage.setItem("yoma_gemini_key",      apiKey);       }, [apiKey]);
  useEffect(() => { if (stabilityKey)  localStorage.setItem("yoma_stability_key",   stabilityKey); }, [stabilityKey]);
  useEffect(() => {                    localStorage.setItem("yoma_tl_provider",     provider);     }, [provider]);
  useEffect(() => {                    localStorage.setItem("yoma_stability_model", stabilityModel);}, [stabilityModel]);
  useEffect(() => {                    localStorage.setItem("yoma_proj_type",       projType);     }, [projType]);
  useEffect(() => {                    localStorage.setItem("yoma_proj_style",      projStyle);    }, [projStyle]);
  useEffect(() => {                    localStorage.setItem("yoma_proj_loc",        projLoc);      }, [projLoc]);
  useEffect(() => {                    localStorage.setItem("yoma_hold_dur",        holdDur);      }, [holdDur]);

  // ── Terrain upload ────────────────────────────────────────────────────────
  function handleTerrainUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => resizeDU(ev.target.result, 1280, 720, url => {
      setTerrainImg(url);
      setPhases(initPhases());
      setVideoUrl(null);
      setError("");
    });
    reader.readAsDataURL(file);
  }

  function resizeDU(src, maxW, maxH, cb) {
    const img = new Image();
    img.onload = () => {
      const r = Math.min(maxW / img.width, maxH / img.height, 1);
      const c = document.createElement("canvas");
      c.width  = Math.round(img.width  * r);
      c.height = Math.round(img.height * r);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      cb(c.toDataURL("image/jpeg", 0.88));
    };
    img.src = src;
  }

  // ── Phase helpers ─────────────────────────────────────────────────────────
  function updPhase(id, patch) {
    setPhases(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  }

  const phasesRef = useRef(phases);
  useEffect(() => { phasesRef.current = phases; }, [phases]);

  function getSource(idx) {
    if (idx === 0) return terrainImg;
    return phasesRef.current[idx - 1]?.image || null;
  }

  // ── Injecter le contexte projet dans le prompt ────────────────────────────
  function buildContextPrompt(basePrompt) {
    const parts = [
      projType  && `Type: ${projType}`,
      projStyle && `Style: ${projStyle}`,
      projLoc   && `Location: ${projLoc}`,
      projName  && `Project: ${projName}`,
    ].filter(Boolean);
    if (!parts.length) return basePrompt;
    return `[PROJECT CONTEXT — ${parts.join(" | ")}]\n\n${basePrompt}`;
  }

  // ── Test clé Gemini ───────────────────────────────────────────────────────
  async function testApiKey() {
    if (!apiKey.trim()) { setTestMsg("⚠ Clé API vide."); setTestResult("error"); return; }
    setTestResult(null); setTestMsg("Test en cours…");
    try {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`,
        { signal: AbortSignal.timeout(8000) }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || `HTTP ${res.status}`);
      const imgModels = (data.models || [])
        .filter(m => m.supportedGenerationMethods?.includes("generateContent"))
        .map(m => m.name.replace("models/", ""));
      setTestResult("ok");
      setTestMsg(`✅ Clé valide — ${imgModels.length} modèles disponibles :\n${imgModels.slice(0, 12).join(", ")}`);
    } catch (e) {
      setTestResult("error");
      setTestMsg(`❌ ${e.message}`);
    }
  }

  // ── Resize SDXL (v1 uniquement) ───────────────────────────────────────────
  function resizeToSDXL(dataUrl) {
    return new Promise(resolve => {
      const SDXL_DIMS = [
        [1024, 1024],[1152, 896],[1216, 832],[1344, 768],[1536, 640],
        [640, 1536],[768, 1344],[832, 1216],[896, 1152],
      ];
      const img = new Image();
      img.onload = () => {
        const srcRatio = img.width / img.height;
        let best = SDXL_DIMS[0], bestDiff = Infinity;
        for (const [w, h] of SDXL_DIMS) {
          const diff = Math.abs(w / h - srcRatio);
          if (diff < bestDiff) { bestDiff = diff; best = [w, h]; }
        }
        const [tw, th] = best;
        const c = document.createElement("canvas");
        c.width = tw; c.height = th;
        c.getContext("2d").drawImage(img, 0, 0, tw, th);
        resolve(c.toDataURL("image/jpeg", 0.90));
      };
      img.src = dataUrl;
    });
  }

  // ── Resize générique ≤ 1024px (v2beta) ───────────────────────────────────
  function resizeTo1024(dataUrl) {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const r = Math.min(1024 / img.width, 1024 / img.height, 1);
        const w = Math.round(img.width * r);
        const h = Math.round(img.height * r);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.92));
      };
      img.src = dataUrl;
    });
  }

  // ── Stability AI — SDXL v1 image-to-image (legacy) ───────────────────────
  async function callStabilitySDXL(prompt, sourceDataUrl) {
    const sdxlDataUrl = await resizeToSDXL(sourceDataUrl);
    const b64  = sdxlDataUrl.split(",")[1];
    const byteArr = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob    = new Blob([byteArr], { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("init_image",        blob, "source.jpg");
    fd.append("init_image_mode",   "IMAGE_STRENGTH");
    fd.append("image_strength",    "0.35");
    fd.append("text_prompts[0][text]",   buildContextPrompt(prompt).slice(0, 2000));
    fd.append("text_prompts[0][weight]", "1");
    fd.append("text_prompts[1][text]",
      "cartoon, illustration, painting, drawing, unrealistic, blurry, distorted perspective, changed camera angle, different viewpoint, different background, different sky, text, watermark, logo");
    fd.append("text_prompts[1][weight]", "-1");
    fd.append("cfg_scale",    "9");
    fd.append("samples",      "1");
    fd.append("steps",        "50");
    fd.append("style_preset", "photographic");
    const res = await fetch(
      "https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/image-to-image",
      {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${stabilityKey}` },
        body: fd,
        signal: AbortSignal.timeout(90_000),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `SDXL HTTP ${res.status}`);
    }
    const data = await res.json();
    const art  = data.artifacts?.[0];
    if (!art?.base64) throw new Error("SDXL : aucune image retournée.");
    return `data:image/png;base64,${art.base64}`;
  }

  // ── Stability AI — Structure Control v2beta (recommandé) ─────────────────
  // Préserve parfaitement l'angle de vue et les éléments de fond
  async function callStabilityCore(prompt, sourceDataUrl) {
    const resized = await resizeTo1024(sourceDataUrl);
    const b64     = resized.split(",")[1];
    const byteArr = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob    = new Blob([byteArr], { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("image",            blob, "source.jpg");
    fd.append("prompt",           buildContextPrompt(prompt).slice(0, 10000));
    fd.append("negative_prompt",  "cartoon, illustration, painting, drawing, unrealistic, blurry, changed camera angle, different background, different sky, different street, text, watermark, logo, deformed");
    fd.append("control_strength", "0.80");
    fd.append("output_format",    "jpeg");
    const res = await fetch(
      "https://api.stability.ai/v2beta/stable-image/control/structure",
      {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${stabilityKey}` },
        body: fd,
        signal: AbortSignal.timeout(90_000),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.errors?.join(", ") || err.message || `Structure Control HTTP ${res.status}`);
    }
    const data = await res.json();
    if (!data.image) throw new Error("Structure Control : aucune image retournée.");
    return `data:image/jpeg;base64,${data.image}`;
  }

  // ── Stability AI — Stable Diffusion 3 image-to-image (premium) ───────────
  async function callStabilitySD3(prompt, sourceDataUrl) {
    const resized = await resizeTo1024(sourceDataUrl);
    const b64     = resized.split(",")[1];
    const byteArr = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob    = new Blob([byteArr], { type: "image/jpeg" });
    const fd = new FormData();
    fd.append("image",           blob, "source.jpg");
    fd.append("mode",            "image-to-image");
    fd.append("strength",        "0.65");
    fd.append("prompt",          buildContextPrompt(prompt).slice(0, 10000));
    fd.append("negative_prompt", "cartoon, illustration, painting, unrealistic, blurry, changed camera angle, different background, text, watermark");
    fd.append("model",           "sd3-medium");
    fd.append("output_format",   "jpeg");
    const res = await fetch(
      "https://api.stability.ai/v2beta/stable-image/generate/sd3",
      {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${stabilityKey}` },
        body: fd,
        signal: AbortSignal.timeout(120_000),
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.errors?.join(", ") || err.message || `SD3 HTTP ${res.status}`);
    }
    const data = await res.json();
    if (!data.image) throw new Error("SD3 : aucune image retournée.");
    return `data:image/jpeg;base64,${data.image}`;
  }

  // ── Dispatch Stability AI selon le modèle sélectionné ────────────────────
  async function callStability(prompt, sourceDataUrl) {
    if (stabilityModel === "sd3")  return callStabilitySD3(prompt,  sourceDataUrl);
    if (stabilityModel === "sdxl") return callStabilitySDXL(prompt, sourceDataUrl);
    return callStabilityCore(prompt, sourceDataUrl); // "core" = défaut
  }

  // ── Gemini — cascade de modèles ───────────────────────────────────────────
  async function callGemini(prompt, sourceDataUrl) {
    const b64  = sourceDataUrl.split(",")[1];
    const mime = sourceDataUrl.split(";")[0].split(":")[1];
    const modelsToTry = model === "__auto__"
      ? ["gemini-2.0-flash-exp-image-generation", "gemini-2.0-flash-preview-image-generation", "gemini-2.0-flash-exp"]
      : [model];
    let lastErr = null;
    for (const m of modelsToTry) {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${m}:generateContent?key=${apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: buildContextPrompt(prompt) }, { inlineData: { mimeType: mime, data: b64 } }] }],
            generationConfig: { responseModalities: ["TEXT", "IMAGE"] },
          }),
          signal: AbortSignal.timeout(120_000),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        lastErr = new Error(`[${m}] ${err.error?.message || "HTTP " + res.status}`);
        continue;
      }
      const data  = await res.json();
      const parts = data.candidates?.[0]?.content?.parts || [];
      const imgP  = parts.find(p => p.inlineData?.mimeType?.startsWith("image/"));
      if (!imgP) { lastErr = new Error(`[${m}] Aucune image dans la réponse.`); continue; }
      return `data:${imgP.inlineData.mimeType};base64,${imgP.inlineData.data}`;
    }
    throw lastErr || new Error("Tous les modèles Gemini ont échoué.");
  }

  // ── Dispatch selon le provider sélectionné ───────────────────────────────
  async function generateWithSource(phase, srcImg) {
    if (!srcImg) { setError("⚠ Image source manquante."); return null; }
    if (provider === "stability" && !stabilityKey.trim()) {
      setError("⚠ Clé Stability AI manquante. Obtenez-en une sur platform.stability.ai");
      return null;
    }
    if (provider === "gemini" && !apiKey.trim()) {
      setError("⚠ Clé API Gemini manquante.");
      return null;
    }
    setError("");
    setGenerating(phase.id);
    updPhase(phase.id, { status: "generating", image: null, validated: false });
    try {
      let url;
      if (provider === "stability") {
        url = await callStability(phase.prompt, srcImg);
      } else {
        try {
          url = await callGemini(phase.prompt, srcImg);
        } catch (geminiErr) {
          if (stabilityKey.trim()) {
            setError(`⚠ Gemini indisponible — Basculement Stability AI… (${geminiErr.message.split("\n")[0].slice(0, 100)})`);
            url = await callStability(phase.prompt, srcImg);
            setError("");
          } else {
            throw new Error(
              `${geminiErr.message}\n\n💡 La génération d'images Gemini nécessite un accès expérimental.\n→ Basculez sur "Stability AI" dans le panneau gauche.`
            );
          }
        }
      }
      updPhase(phase.id, { image: url, status: "done" });
      return url;
    } catch (e) {
      updPhase(phase.id, { status: "error" });
      setError(`❌ Phase ${phase.id} : ${e.message}`);
      return null;
    } finally {
      setGenerating(null);
    }
  }

  function generatePhase(idx) {
    return generateWithSource(phases[idx], getSource(idx));
  }

  async function generateAll() {
    if (!terrainImg) { setError("⚠ Uploadez d'abord la photo du terrain."); return; }
    let lastImg = terrainImg;
    for (let i = 0; i < phases.length; i++) {
      const ph = phasesRef.current[i];
      if (ph.status === "done" && ph.validated && ph.image) { lastImg = ph.image; continue; }
      const result = await generateWithSource(ph, lastImg);
      if (!result) break;
      lastImg = result;
    }
  }

  // ── Assemble video ────────────────────────────────────────────────────────
  async function assembleVideo() {
    const imgs   = [terrainImg, ...phases.map(p => p.image)].filter(Boolean);
    const labels = ["Terrain original", ...phases.map(p => `${p.icon} ${p.name}`)];
    if (imgs.length < 2) { setError("⚠ Générez au moins une phase avant d'assembler."); return; }
    setAssembling(true); setError(""); setVideoUrl(null); setAsmPct(0);
    try {
      const url = await renderVideo(imgs, labels, pct => setAsmPct(pct), projName, holdDur);
      setVideoUrl(url);
    } catch (e) {
      setError(`❌ Assemblage : ${e.message}`);
    } finally {
      setAssembling(false);
    }
  }

  // ── Download helpers ──────────────────────────────────────────────────────
  function dlImg(dataUrl, name) {
    const a = document.createElement("a"); a.href = dataUrl; a.download = name; a.click();
  }
  function dlVideo() {
    if (!videoUrl) return;
    const a = document.createElement("a");
    a.href = videoUrl;
    a.download = `YOMA_TimeLapse_${(projName || "Projet").replace(/\s+/g, "_")}.webm`;
    a.click();
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const doneCount      = phases.filter(p => p.status === "done").length;
  const validatedCount = phases.filter(p => p.validated).length;
  const busyGlobal     = !!generating || assembling;
  const providerLabel  = provider === "stability"
    ? (stabilityModel === "sd3" ? "SD3" : stabilityModel === "core" ? "Structure Core" : "SDXL")
    : "Gemini";

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: WARM, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        .tl-btn{border:none;border-radius:8px;padding:10px 18px;cursor:pointer;font-size:13px;font-weight:600;transition:all .15s;font-family:'DM Sans',sans-serif;}
        .tl-btn:hover:not(:disabled){opacity:.85;transform:translateY(-1px);}
        .tl-btn:disabled{opacity:.4;cursor:default;transform:none;}
        .tl-inp{background:${WHITE};border:1px solid ${BORDER};border-radius:6px;padding:8px 12px;font-size:13px;color:${INK};width:100%;box-sizing:border-box;font-family:'DM Sans',sans-serif;}
        .tl-inp:focus{outline:none;border-color:${GOLD};}
        .tl-sel{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);color:${WHITE};border-radius:6px;padding:7px 10px;font-size:11px;font-family:'DM Sans',sans-serif;width:100%;cursor:pointer;}
        .tl-sel option{background:${INK};color:${WHITE};}
        .ph-card{background:${WHITE};border-radius:16px;border:1px solid ${BORDER};overflow:hidden;margin-bottom:20px;transition:border-color .2s,box-shadow .2s;}
        .ph-card:hover{border-color:${GOLD};box-shadow:0 4px 20px rgba(0,0,0,.06);}
        .drop-zone{border:2px dashed ${BORDER};border-radius:12px;cursor:pointer;transition:all .2s;text-align:center;}
        .drop-zone:hover{border-color:${GOLD};background:${SOFT};}
        .strip-thumb{border-radius:8px;overflow:hidden;border:2px solid ${BORDER};transition:border-color .2s;}
        .strip-thumb:hover{border-color:${GOLD};}
        textarea.tl-inp{resize:vertical;line-height:1.6;font-size:12px;}
        .sidebar-sep{height:1px;background:rgba(255,255,255,.08);margin:14px 0;}
      `}</style>

      {/* ═══════ SIDEBAR ═══════ */}
      <aside style={{ width: 248, background: INK, display: "flex", flexDirection: "column", padding: "24px 14px", flexShrink: 0, overflowY: "auto" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ color: GOLD, fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, letterSpacing: 3 }}>YOMA</div>
          <div style={{ color: "#5A4E46", fontSize: 9, letterSpacing: "0.22em", marginTop: 3 }}>TIME-LAPSE IA</div>
        </div>

        {/* Projet */}
        <div style={{ marginBottom: 12 }}>
          <Label>PROJET</Label>
          <input className="tl-inp" placeholder="Nom du projet…" value={projName} onChange={e => setProjName(e.target.value)}
            style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)", color: WHITE, marginBottom: 6 }} />
          <input className="tl-inp" placeholder="Nom du client…" value={clientNm} onChange={e => setClientNm(e.target.value)}
            style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)", color: WHITE }} />
        </div>

        {/* Contexte projet ─ injecté dans tous les prompts IA */}
        <div style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(201,169,110,.08)", borderRadius: 8, border: "1px solid rgba(201,169,110,.15)" }}>
          <Label gold>CONTEXTE PROJET IA</Label>
          <div style={{ fontSize: 9, color: "#8A7A6A", marginBottom: 8, lineHeight: 1.5 }}>
            Ces informations sont injectées dans chaque prompt pour des rendus personnalisés
          </div>
          <select className="tl-sel" value={projType} onChange={e => setProjType(e.target.value)} style={{ marginBottom: 6 }}>
            {PROJ_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="tl-sel" value={projStyle} onChange={e => setProjStyle(e.target.value)} style={{ marginBottom: 6 }}>
            {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            value={projLoc} onChange={e => setProjLoc(e.target.value)}
            placeholder="Localisation (ex: Casablanca)"
            style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", color: WHITE, borderRadius: 6, padding: "6px 10px", fontSize: 11, fontFamily: "'DM Sans',sans-serif", width: "100%", boxSizing: "border-box" }}
          />
        </div>

        <div className="sidebar-sep" />

        {/* Provider toggle */}
        <div style={{ marginBottom: 10 }}>
          <Label>MOTEUR IA</Label>
          <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,.1)" }}>
            {[
              { val: "stability", label: "Stability AI", emoji: "⚡" },
              { val: "gemini",    label: "Gemini",       emoji: "🤖" },
            ].map(opt => (
              <button key={opt.val} onClick={() => setProvider(opt.val)}
                style={{ flex: 1, border: "none", padding: "7px 4px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                  background: provider === opt.val ? GOLD : "rgba(255,255,255,.05)",
                  color:      provider === opt.val ? INK  : "#9A8A80",
                  transition: "all .15s" }}>
                {opt.emoji} {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stability AI */}
        {provider === "stability" && (
          <div style={{ marginBottom: 12 }}>
            {/* Modèle Stability */}
            <div style={{ marginBottom: 8 }}>
              <Label>MODÈLE STABILITY AI</Label>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[
                  { val: "core", label: "⚡ Structure Core", desc: "v2beta — recommandé, préserve l'angle de vue", badge: "RECOMMANDÉ" },
                  { val: "sd3",  label: "✨ SD3 Medium",     desc: "v2beta — qualité maximale, +lent, +crédits", badge: "PREMIUM" },
                  { val: "sdxl", label: "🔧 SDXL v1",        desc: "v1 — compatibilité large, qualité standard", badge: null },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setStabilityModel(opt.val)}
                    style={{
                      border: `1.5px solid ${stabilityModel === opt.val ? GOLD : "rgba(255,255,255,.1)"}`,
                      background: stabilityModel === opt.val ? "rgba(201,169,110,.12)" : "rgba(255,255,255,.03)",
                      borderRadius: 7, padding: "7px 10px", cursor: "pointer", textAlign: "left",
                      fontFamily: "'DM Sans',sans-serif",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: stabilityModel === opt.val ? GOLD : WHITE }}>{opt.label}</span>
                      {opt.badge && (
                        <span style={{ fontSize: 8, background: opt.badge === "RECOMMANDÉ" ? GOLD : "#6A3A80", color: WHITE, borderRadius: 3, padding: "1px 5px", fontWeight: 800 }}>{opt.badge}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: "#7A6A5A", marginTop: 2 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Clé */}
            <div style={{ padding: "10px", background: "rgba(201,169,110,.08)", borderRadius: 8, border: "1px solid rgba(201,169,110,.15)" }}>
              <Label gold>CLÉ STABILITY AI</Label>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input type={showKey ? "text" : "password"} value={stabilityKey} onChange={e => setStabilityKey(e.target.value)}
                  placeholder="sk-…"
                  style={{ flex: 1, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", color: WHITE, borderRadius: 6, padding: "6px 10px", fontSize: 11, fontFamily: "monospace" }} />
                <button onClick={() => setShowKey(v => !v)}
                  style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14, padding: "0 2px" }}>{showKey ? "🙈" : "👁"}</button>
              </div>
              <a href="https://platform.stability.ai/account/keys" target="_blank" rel="noopener noreferrer"
                style={{ color: GOLD, fontSize: 10 }}>→ platform.stability.ai</a>
            </div>
          </div>
        )}

        {/* Gemini */}
        {provider === "gemini" && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8, padding: "8px 10px", background: "rgba(230,100,50,.08)", border: "1px solid rgba(230,100,50,.2)", borderRadius: 7, fontSize: 9, color: "#E07050", lineHeight: 1.55 }}>
              ⚠ La génération d'images Gemini nécessite un accès expérimental.
              {stabilityKey.trim()
                ? <span style={{ color: "#6CAF70" }}><br/>✓ Fallback Stability AI activé.</span>
                : <span><br/>Ajoutez une clé Stability AI pour le fallback.</span>
              }
            </div>
            <div style={{ padding: "10px", background: "rgba(201,169,110,.08)", borderRadius: 8, border: "1px solid rgba(201,169,110,.15)", marginBottom: 8 }}>
              <Label gold>CLÉ API GEMINI</Label>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input type={showKey ? "text" : "password"} value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder="AIza…"
                  style={{ flex: 1, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", color: WHITE, borderRadius: 6, padding: "6px 10px", fontSize: 11, fontFamily: "monospace" }} />
                <button onClick={() => setShowKey(v => !v)}
                  style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14, padding: "0 2px" }}>{showKey ? "🙈" : "👁"}</button>
              </div>
              <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer"
                style={{ color: GOLD, fontSize: 10 }}>→ Google AI Studio</a>
            </div>
            <Label>MODÈLE GEMINI</Label>
            <select value={model} onChange={e => { setModel(e.target.value); setTestResult(null); setTestMsg(""); }} className="tl-sel" style={{ marginBottom: 6 }}>
              <option value="gemini-2.0-flash-exp-image-generation">gemini-2.0-flash-exp-image-generation</option>
              <option value="__auto__">🔄 Auto (cascade)</option>
              <option value="gemini-2.0-flash-preview-image-generation">gemini-2.0-flash-preview-image-generation</option>
              <option value="gemini-2.0-flash-exp">gemini-2.0-flash-exp</option>
            </select>
            <button onClick={testApiKey} disabled={!apiKey.trim()}
              style={{ width: "100%", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "#BBA990", borderRadius: 6, padding: "6px", fontSize: 11, cursor: "pointer", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
              🔍 Tester la clé
            </button>
            {testMsg && (
              <div style={{ marginTop: 6, background: testResult === "ok" ? "rgba(76,175,80,.1)" : "rgba(184,92,58,.1)", border: `1px solid ${testResult === "ok" ? "rgba(76,175,80,.3)" : "rgba(184,92,58,.3)"}`, borderRadius: 6, padding: "6px 8px", fontSize: 9, color: testResult === "ok" ? "#7CB87E" : "#E07050", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {testMsg}
              </div>
            )}
          </div>
        )}

        <div className="sidebar-sep" />

        {/* Progression */}
        <div style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(255,255,255,.03)", borderRadius: 8 }}>
          <Label>PROGRESSION</Label>
          {phases.map(p => (
            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <span style={{ color: "#9A8A80", fontSize: 12 }}>{p.icon} Ph.{p.id}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color:
                p.status === "done"       ? (p.validated ? "#4CAF50" : "#7CB87E") :
                p.status === "generating" ? GOLD :
                p.status === "error"      ? "#E57373" : "#4A3E35" }}>
                {p.status === "done"       ? (p.validated ? "✓ Validé" : "✓ Généré") :
                 p.status === "generating" ? "⏳ En cours…" :
                 p.status === "error"      ? "✗ Erreur" : "○ En attente"}
              </span>
            </div>
          ))}
          <div style={{ marginTop: 8, height: 4, background: "rgba(255,255,255,.07)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(doneCount / 5) * 100}%`, background: GOLD, borderRadius: 2, transition: "width .5s" }} />
          </div>
          <div style={{ color: "#4A3E35", fontSize: 10, marginTop: 5 }}>{doneCount}/5 phases · {validatedCount} validées</div>
        </div>

        {/* Durée vidéo */}
        <div style={{ marginBottom: 12 }}>
          <Label>DURÉE PAR PHASE</Label>
          <div style={{ display: "flex", gap: 4 }}>
            {[2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setHoldDur(s)}
                style={{ flex: 1, border: `1.5px solid ${holdDur === s ? GOLD : "rgba(255,255,255,.1)"}`, background: holdDur === s ? "rgba(201,169,110,.15)" : "rgba(255,255,255,.03)", color: holdDur === s ? GOLD : "#7A6A5A", borderRadius: 6, padding: "5px 0", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                {s}s
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
          <button className="tl-btn" onClick={generateAll} disabled={!terrainImg || busyGlobal}
            style={{ background: GOLD, color: INK }}>
            {generating ? `⏳ Phase ${generating}…` : "▶ Générer tout (auto)"}
          </button>
          <button className="tl-btn" onClick={assembleVideo} disabled={doneCount === 0 || busyGlobal}
            style={{ background: doneCount > 0 ? "#1C3A5C" : "rgba(255,255,255,.04)", color: WHITE, border: "1px solid rgba(255,255,255,.1)" }}>
            {assembling ? `🎬 ${asmPct}%…` : "🎬 Assembler la vidéo"}
          </button>
          {videoUrl && (
            <button className="tl-btn" onClick={dlVideo}
              style={{ background: "rgba(201,169,110,.12)", color: GOLD, border: "1px solid rgba(201,169,110,.3)" }}>
              ⬇ Télécharger .webm
            </button>
          )}
        </div>
      </aside>

      {/* ═══════ MAIN ═══════ */}
      <main style={{ flex: 1, padding: "28px 32px", overflow: "auto", maxWidth: "calc(100vw - 248px)" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 700, color: INK }}>
            Visualisation Temporelle IA
          </div>
          <div style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>
            Time-lapse prédictif en 5 phases ·{" "}
            <span style={{ background: provider === "stability" ? "rgba(201,169,110,.15)" : "rgba(100,180,100,.1)", color: provider === "stability" ? GOLD : "#4CAF50", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
              {provider === "stability" ? `⚡ Stability ${providerLabel}` : "🤖 Gemini"}
            </span>
            <span style={{ color: BORDER, margin: "0 8px" }}>·</span>
            <span style={{ color: MUTED, fontSize: 12 }}>Contexte : {projType} · {projStyle} · {projLoc}</span>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 16px", marginBottom: 20, color: "#B85C3A", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ whiteSpace: "pre-wrap" }}>{error}</span>
            <button onClick={() => setError("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#B85C3A", fontSize: 18, lineHeight: 1, flexShrink: 0, marginLeft: 12 }}>×</button>
          </div>
        )}

        {/* ── Terrain upload ── */}
        <div style={{ marginBottom: 28 }}>
          <SectionTitle>📸 Photo du terrain (image source)</SectionTitle>
          {terrainImg ? (
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{ position: "relative" }}>
                <img src={terrainImg} alt="Terrain" style={{ height: 180, borderRadius: 12, border: `2px solid ${GOLD}`, objectFit: "cover", display: "block" }} />
                <div style={{ position: "absolute", top: 8, left: 8, background: GOLD, color: INK, borderRadius: 4, padding: "2px 10px", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em" }}>SOURCE</div>
                <button onClick={() => terrainRef.current.click()}
                  style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(28,25,21,.8)", color: WHITE, border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                  ↺ Changer
                </button>
              </div>
              <div style={{ background: WHITE, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 18px", fontSize: 12, color: MUTED, maxWidth: 300 }}>
                <div style={{ fontWeight: 700, color: INK, marginBottom: 8 }}>Chaîne de génération IA</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {["📸 Terrain → Ph.1", "Ph.1 → Ph.2", "Ph.2 → Ph.3", "Ph.3 → Ph.4", "Ph.4 → Ph.5 🏁"].map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: i < doneCount ? GOLD : BORDER, flexShrink: 0 }} />
                      <span style={{ color: i < doneCount ? INK : MUTED }}>{s}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BORDER}`, fontSize: 11 }}>
                  <strong style={{ color: INK }}>Contexte injecté :</strong><br/>
                  <span style={{ color: MUTED }}>{projType} · {projStyle}</span><br/>
                  <span style={{ color: MUTED }}>{projLoc}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="drop-zone" style={{ padding: "36px 24px" }} onClick={() => terrainRef.current.click()}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>🏞️</div>
              <div style={{ fontWeight: 600, color: INK, marginBottom: 4 }}>Cliquez pour charger la photo du terrain</div>
              <div style={{ fontSize: 12, color: MUTED }}>JPG, PNG — résolution recommandée : 1280×720 minimum</div>
            </div>
          )}
          <input ref={terrainRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleTerrainUpload} />
        </div>

        {/* ── Film strip ── */}
        {(terrainImg || doneCount > 0) && (
          <div style={{ marginBottom: 28 }}>
            <SectionTitle>🎞️ Film strip — aperçu de la chaîne</SectionTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 8, overflowX: "auto", paddingBottom: 8 }}>
              <Thumb img={terrainImg} label="Terrain" accent={GOLD} />
              {phases.map((ph, idx) => (
                <div key={ph.id} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <div style={{ color: MUTED, fontSize: 18, flexShrink: 0 }}>→</div>
                  <Thumb
                    img={ph.image}
                    label={`Ph.${ph.id}`}
                    icon={ph.status === "generating" ? "⏳" : ph.icon}
                    accent={ph.status === "done" ? ph.accent : BORDER}
                    validated={ph.validated}
                    loading={ph.status === "generating"}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Phase cards ── */}
        {phases.map((phase, idx) => {
          const srcImg   = getSource(idx);
          const canGen   = !!srcImg && !busyGlobal;
          const isGen    = generating === phase.id;
          const st       = ST[phase.status];
          const expanded = expandedPh === phase.id;

          return (
            <div key={phase.id} className="ph-card">

              {/* Phase header */}
              <div style={{
                background: `linear-gradient(135deg, ${phase.accent}18, transparent)`,
                borderBottom: `1px solid ${BORDER}`,
                padding: "16px 20px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${phase.accent}22`, border: `1.5px solid ${phase.accent}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                    {phase.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: INK, fontSize: 15 }}>Phase {phase.id} — {phase.name}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>
                      {idx === 0 ? "Source : photo terrain uploadée" : `Source : rendu Phase ${idx}`}
                    </div>
                  </div>
                </div>
                <span style={{ background: st.bg, color: st.color, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                  {isGen ? `⏳ Génération ${providerLabel}…` : st.label}
                </span>
              </div>

              {/* Images comparison */}
              <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.1em", marginBottom: 8 }}>
                    {idx === 0 ? "TERRAIN ORIGINAL" : `RENDU PHASE ${idx}`}
                  </div>
                  <ImgBox src={srcImg} placeholder={idx === 0 ? "⬆ Uploadez le terrain" : `Générez d'abord la Phase ${idx}`} />
                </div>

                <div style={{ textAlign: "center", color: phase.status === "done" ? phase.accent : BORDER, fontSize: 28, fontWeight: 700, paddingTop: 24 }}>→</div>

                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: "0.1em", marginBottom: 8 }}>
                    RENDU PHASE {phase.id} — {phase.name.toUpperCase()}
                  </div>
                  <ImgBox
                    src={phase.image}
                    accent={phase.image ? phase.accent : undefined}
                    loading={isGen}
                    providerLabel={providerLabel}
                    placeholder={canGen ? `Cliquez « Générer Phase ${phase.id} »` : idx === 0 ? "Uploadez le terrain d'abord" : `Générez d'abord la Phase ${idx}`}
                    onDownload={phase.image ? () => dlImg(phase.image, `YOMA_Phase${phase.id}_${phase.name.replace(/\s+/g,"_")}.jpg`) : null}
                  />
                </div>
              </div>

              {/* Note */}
              <div style={{ padding: "0 20px 12px" }}>
                <input
                  className="tl-inp" placeholder="📝 Note sur ce rendu (optionnel)…"
                  value={phase.note} onChange={e => updPhase(phase.id, { note: e.target.value })}
                  style={{ fontSize: 12 }}
                />
              </div>

              {/* Prompt editor */}
              <div style={{ padding: "0 20px 16px" }}>
                <button onClick={() => setExpandedPh(expanded ? null : phase.id)}
                  style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 12px", fontSize: 11, color: MUTED, cursor: "pointer", marginBottom: expanded ? 8 : 0 }}>
                  {expanded ? "▲ Masquer le prompt" : "▼ Voir / modifier le prompt IA"}
                </button>
                {expanded && (
                  <>
                    <textarea className="tl-inp" rows={9} value={phase.prompt}
                      onChange={e => updPhase(phase.id, { prompt: e.target.value })} />
                    <button onClick={() => updPhase(phase.id, { prompt: phase.masterPrompt })}
                      style={{ marginTop: 6, background: "none", border: `1px solid ${BORDER}`, borderRadius: 5, padding: "4px 10px", fontSize: 11, color: MUTED, cursor: "pointer" }}>
                      ↺ Réinitialiser au Master Prompt YOMA
                    </button>
                  </>
                )}
              </div>

              {/* Phase actions */}
              <div style={{ padding: "12px 20px 18px", borderTop: `1px solid ${BORDER}`, background: SOFT, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button className="tl-btn" onClick={() => generatePhase(idx)}
                  disabled={!canGen}
                  style={{ background: canGen ? phase.accent : BORDER, color: WHITE, flex: 1, minWidth: 160 }}>
                  {isGen ? `⏳ ${providerLabel}…`
                   : phase.image ? `↺ Regénérer (${providerLabel})`
                   : `▶ Générer Phase ${phase.id}`}
                </button>

                <label title="Uploader une image manuellement pour cette phase"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "10px 14px", borderRadius: 8, border: `1px solid ${BORDER}`, background: WHITE, color: MUTED, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                  📁 Upload
                  <input type="file" accept="image/*" style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => updPhase(phase.id, { image: ev.target.result, status: "done", validated: false });
                      reader.readAsDataURL(file);
                      e.target.value = "";
                    }} />
                </label>

                {phase.status === "done" && (
                  <button className="tl-btn"
                    onClick={() => updPhase(phase.id, { validated: !phase.validated })}
                    style={{ background: phase.validated ? "#4CAF50" : WHITE, color: phase.validated ? WHITE : MUTED, border: `1.5px solid ${phase.validated ? "#4CAF50" : BORDER}` }}>
                    {phase.validated ? "✓ Validé" : "○ Valider"}
                  </button>
                )}

                {phase.image && (
                  <button className="tl-btn"
                    onClick={() => dlImg(phase.image, `YOMA_Phase${phase.id}_${phase.name.replace(/\s+/g,"_")}.jpg`)}
                    style={{ background: "none", border: `1px solid ${BORDER}`, color: MUTED }}>
                    ⬇
                  </button>
                )}

                {phase.status === "error" && (
                  <button className="tl-btn" onClick={() => generatePhase(idx)} disabled={!canGen}
                    style={{ background: "#FFEBEE", color: "#B85C3A", border: "1px solid #FFCDD2" }}>
                    🔄 Réessayer
                  </button>
                )}

                {phase.status === "done" && (
                  <div style={{ fontSize: 11, color: phase.validated ? "#4CAF50" : MUTED, width: "100%", marginTop: 2 }}>
                    {phase.validated ? "✓ Validé — alimente automatiquement la phase suivante" : "Validez pour confirmer et alimenter la phase suivante"}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* ── Assemblage progress ── */}
        {assembling && (
          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, color: INK, marginBottom: 10 }}>🎬 Assemblage vidéo — {asmPct}%</div>
            <div style={{ height: 8, background: SOFT, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${asmPct}%`, background: GOLD, borderRadius: 4, transition: "width .3s" }} />
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>Rendu frame par frame avec transitions fondu · durée {holdDur}s/phase…</div>
          </div>
        )}

        {/* ── Video player ── */}
        {videoUrl && (
          <div style={{ background: INK, borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", color: GOLD, fontSize: 20, fontWeight: 700 }}>
                🎬 {projName || "Projet YOMA"} — Time-Lapse Prédictif
              </div>
              <div style={{ color: "#5A4E46", fontSize: 11 }}>WebM · {holdDur}s/phase · {doneCount + 1} images</div>
            </div>
            <video ref={videoRef} src={videoUrl} controls
              style={{ width: "100%", borderRadius: 10, maxHeight: 500, background: "#000" }} />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button className="tl-btn" onClick={dlVideo} style={{ background: GOLD, color: INK }}>⬇ Télécharger la vidéo</button>
              <button className="tl-btn" onClick={() => videoRef.current?.play()}
                style={{ background: "rgba(255,255,255,.06)", color: WHITE, border: "1px solid rgba(255,255,255,.15)" }}>▶ Lecture</button>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4CAF50" }} />
                <span style={{ color: "#4CAF50", fontSize: 12, fontWeight: 600 }}>Vidéo prête</span>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

// ── Sous-composants ───────────────────────────────────────────────────────────
function Label({ children, gold }) {
  return (
    <div style={{ color: gold ? GOLD : "#5A4E46", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 6 }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 700, color: "#1C1915", fontSize: 14, marginBottom: 12 }}>{children}</div>
  );
}

function Thumb({ img, label, icon, accent, validated, loading }) {
  return (
    <div style={{ flexShrink: 0, textAlign: "center" }}>
      <div className="strip-thumb" style={{ width: 100, height: 68, background: "#F2EDE6", position: "relative", borderColor: accent || "#E7E1D8" }}>
        {img
          ? <img src={img} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: loading ? 22 : 24 }}>
              {loading ? "⏳" : (icon || "🏞️")}
            </div>
        }
        {validated && (
          <div style={{ position: "absolute", top: 3, right: 3, background: "#4CAF50", color: "#fff", borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>✓</div>
        )}
      </div>
      <div style={{ fontSize: 10, color: "#8B7361", marginTop: 4, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function ImgBox({ src, accent, loading, providerLabel, placeholder, onDownload }) {
  return (
    <div style={{
      height: 200, background: "#F2EDE6", borderRadius: 10, overflow: "hidden",
      border: `2px solid ${accent || "#E7E1D8"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative",
    }}>
      {src ? (
        <>
          <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {onDownload && (
            <button onClick={onDownload}
              style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(28,25,21,.75)", color: "#fff", border: "none", borderRadius: 5, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
              ⬇
            </button>
          )}
        </>
      ) : loading ? (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚙️</div>
          <div style={{ color: "#8B7361", fontSize: 12 }}>{providerLabel || "IA"} génère le rendu…</div>
          <div style={{ color: "#8B7361", fontSize: 11, marginTop: 3 }}>30 à 90 secondes</div>
        </div>
      ) : (
        <div style={{ textAlign: "center", color: "#8B7361", fontSize: 12, padding: "0 20px" }}>{placeholder}</div>
      )}
    </div>
  );
}

// ── Renderer vidéo (Canvas + MediaRecorder) ───────────────────────────────────
async function renderVideo(dataUrls, labels, onPct, projName = "YOMA", holdSec = 3) {
  const imgs = await Promise.all(dataUrls.map(src => new Promise((res, rej) => {
    const img = new Image();
    img.onload  = () => res(img);
    img.onerror = () => rej(new Error("Image non chargeable"));
    img.src = src;
  })));

  const W = 1280, H = 720;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  const FPS     = 25;
  const HOLD_F  = holdSec * FPS;
  const FADE_F  = Math.round(1.2 * FPS);      // 1.2s fondu doux
  const PHASE_F = HOLD_F + FADE_F;
  const TOTAL_F = imgs.length * HOLD_F + (imgs.length - 1) * FADE_F;

  const mime = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"]
    .find(t => MediaRecorder.isTypeSupported(t)) || "video/webm";

  const stream   = canvas.captureStream(FPS);
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
  const chunks   = [];
  recorder.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);

  let framesDone = 0;

  function drawFrame(imgIdx, frame) {
    // Image courante
    ctx.drawImage(imgs[imgIdx], 0, 0, W, H);

    // Fondu vers l'image suivante
    if (frame >= HOLD_F && imgIdx + 1 < imgs.length) {
      const t = Math.min((frame - HOLD_F) / FADE_F, 1);
      // Ease in-out pour un fondu plus doux
      const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      ctx.globalAlpha = ease;
      ctx.drawImage(imgs[imgIdx + 1], 0, 0, W, H);
      ctx.globalAlpha = 1;
    }

    // Bande inférieure YOMA
    const grad = ctx.createLinearGradient(0, H - 70, 0, H);
    grad.addColorStop(0, "rgba(28,25,21,0)");
    grad.addColorStop(0.4, "rgba(28,25,21,0.75)");
    grad.addColorStop(1, "rgba(28,25,21,0.92)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, H - 70, W, 70);

    // Logo YOMA (Playfair-like avec letter-spacing)
    ctx.fillStyle = "#C9A96E";
    ctx.font      = "bold 14px Georgia, serif";
    ctx.letterSpacing = "3px";
    ctx.fillText("YOMA DESIGN", 20, H - 38);
    ctx.letterSpacing = "0px";

    // Séparateur
    ctx.fillStyle = "rgba(201,169,110,0.45)";
    ctx.fillRect(18, H - 27, 90, 1);

    // Nom projet
    ctx.fillStyle = "rgba(255,255,255,0.50)";
    ctx.font      = "11px 'DM Sans', system-ui, sans-serif";
    ctx.fillText(projName || "YOMA", 20, H - 13);

    // Compteur de phase (n / total-1)
    const phaseNum  = Math.min(imgIdx, imgs.length - 1);
    const phaseTotal = imgs.length - 1;
    const counterTxt = imgIdx === 0
      ? "Terrain original"
      : `Phase ${phaseNum} / ${phaseTotal}`;
    ctx.fillStyle = "#FFFFFF";
    ctx.font      = "bold 15px Georgia, serif";
    const lw = ctx.measureText(counterTxt).width;
    ctx.fillText(counterTxt, (W - lw) / 2, H - 22);

    // Label complet de la phase
    const lbl    = labels[imgIdx] || "";
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.font      = "12px system-ui, sans-serif";
    const lblW   = ctx.measureText(lbl).width;
    ctx.fillText(lbl, (W - lblW) / 2, H - 7);

    // Barre de progression GOLD
    framesDone++;
    const pct = framesDone / TOTAL_F;
    ctx.fillStyle = "rgba(201,169,110,0.25)";
    ctx.fillRect(0, H - 3, W, 3);
    ctx.fillStyle = "#C9A96E";
    ctx.fillRect(0, H - 3, W * pct, 3);

    onPct(Math.min(99, Math.round(pct * 100)));
  }

  return new Promise((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mime });
      resolve(URL.createObjectURL(blob));
    };
    recorder.onerror = e => reject(new Error(e.error?.message || "Erreur MediaRecorder"));
    recorder.start(200);

    let imgIdx = 0, frame = 0;

    function tick() {
      if (imgIdx >= imgs.length) {
        setTimeout(() => recorder.stop(), 400);
        return;
      }
      drawFrame(imgIdx, frame);
      frame++;
      const limit = imgIdx < imgs.length - 1 ? PHASE_F : HOLD_F;
      if (frame >= limit) { imgIdx++; frame = 0; }
      setTimeout(tick, 1000 / FPS);
    }

    tick();
  });
}
